import { useCallback, useContext, useRef, useState } from "react";
import UserContext from "common/Context/UserContext";
import { useEffectiveAccess } from "common/Context/EffectiveAccessContext";
import { hasDataManagementAccess } from "common/Utilities/EffectiveAccess.utils";
import { NODE_CONTEXT_IMPORT } from "./NodeContextImportConstants";
import Toast from "common/Components/Toast/Toast";
import {
  postContextImportValidate,
  postContextImportSync,
  CONTEXT_IMPORT_MAX_BYTES,
} from "./ContextImportApi";
import {
  buildImportToastFailure,
  buildImportToastSuccess,
  hideImportToast,
  IMPORT_TOAST_DEFAULT_DURATION,
} from "./ImportContextToast";
import {
  ImportsPageRoot,
  ImportPrimaryCard,
  ImportCardShell,
  CardTitle,
  FieldLabel,
  FileRow,
  ActionRow,
  FileNameBox,
  UploadButton,
  ClearLink,
  StatusBody,
  STATUS_TINT,
} from "./NodeContextImports.styles";
import {
  isValidNodeContextJsonDocument,
  NODE_CONTEXT_JSON_SHAPE_ERROR,
} from "./NodeContextFileValidation";
import {
  formatValidateApiResponseToStatus,
  formatSyncApiResponseToStatus,
} from "./ContextImportResponseFormat";

const ACCEPT_EXT = /\.(txt|json)$/i;

function isAllowedExtension(name) {
  return ACCEPT_EXT.test(name || "");
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const { internal } = NODE_CONTEXT_IMPORT;
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(new Error(internal.readFileError));
    reader.readAsText(file);
  });
}

function readFileAsArrayBuffer(file) {
  if (typeof file.arrayBuffer === "function") {
    return file.arrayBuffer();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const { internal } = NODE_CONTEXT_IMPORT;
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(internal.readFileError));
    reader.readAsArrayBuffer(file);
  });
}

function resetFileInput(inputRef) {
  if (inputRef.current) inputRef.current.value = "";
}

function fileFromStoredPayload(payload) {
  if (!payload) return null;
  return new File([payload.buffer], payload.name, { type: payload.type });
}

function axiosErrorMessage(err) {
  const { requestFailed } = NODE_CONTEXT_IMPORT.serverMessages;
  const d = err?.response?.data;
  if (typeof d === "string") return d;
  if (d && typeof d.message === "string") return d.message;
  if (d && typeof d.detail === "string") return d.detail;
  if (d && typeof d.error === "string") return d.error;
  return err?.message || requestFailed;
}

export default function NodeContextImports() {
  const nci = NODE_CONTEXT_IMPORT;
  const { userProfile } = useContext(UserContext);
  const profile = userProfile?.profile ?? {};
  const { canAccessPage, usesEffectiveAccess } = useEffectiveAccess();
  const canAccessDataManagement = hasDataManagementAccess(profile, {
    usesEffectiveAccess,
    canAccessPage,
  });

  const inputRef = useRef(null);
  const filePayloadRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [validationPassed, setValidationPassed] = useState(false);
  const [validating, setValidating] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [statusTier, setStatusTier] = useState("neutral");
  const [statusMessage, setStatusMessage] = useState(
    canAccessDataManagement
      ? nci.sessionStatus.neutral
      : nci.sessionStatus.accessRestricted
  );
  const [toast, setToast] = useState(hideImportToast());

  const showToast = useCallback((partial) => {
    setToast((prev) => ({ ...prev, ...partial }));
  }, []);

  const resetSessionStatus = useCallback(() => {
    setValidationPassed(false);
    setStatusTier("neutral");
    const s = NODE_CONTEXT_IMPORT.sessionStatus;
    setStatusMessage(
      canAccessDataManagement ? s.neutral : s.accessRestricted
    );
  }, [canAccessDataManagement]);

  const applyClientValidationFailure = useCallback(
    (message) => {
      setValidationPassed(false);
      setStatusTier("error");
      setStatusMessage(message);
      showToast(buildImportToastFailure(message));
    },
    [showToast]
  );

  const runServerValidate = useCallback(async () => {
    const file = fileFromStoredPayload(filePayloadRef.current);
    if (!file) return;
    setValidating(true);
    setToast(hideImportToast());
    setValidationPassed(false);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const { data } = await postContextImportValidate(fd);
      const formatted = formatValidateApiResponseToStatus(data);
      setStatusMessage(formatted.display);
      setStatusTier(formatted.tier);
      setValidationPassed(formatted.isValid);
      if (formatted.isValid) {
        showToast(buildImportToastSuccess(nci.toast.validationCompleted));
      } else {
        showToast(buildImportToastFailure(nci.toast.validationDidNotPass));
      }
    } catch (err) {
      setValidationPassed(false);
      const msg = axiosErrorMessage(err);
      setStatusTier("error");
      setStatusMessage(
        `${nci.serverMessages.validationFailedPrefix}${msg}`
      );
      showToast(buildImportToastFailure(msg));
    } finally {
      setValidating(false);
    }
  }, [nci, showToast]);

  const handleFileChosen = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setToast(hideImportToast());
      if (!canAccessDataManagement) return;

      if (!isAllowedExtension(file.name)) {
        applyClientValidationFailure(nci.clientValidation.wrongExtension);
        filePayloadRef.current = null;
        setSelectedFile(null);
        resetFileInput(inputRef);
        return;
      }
      if (file.size > CONTEXT_IMPORT_MAX_BYTES) {
        applyClientValidationFailure(nci.clientValidation.fileTooLarge);
        filePayloadRef.current = null;
        setSelectedFile(null);
        resetFileInput(inputRef);
        return;
      }

      if (/\.json$/i.test(file.name)) {
        try {
          const text = await readFileAsText(file);
          const parsed = JSON.parse(text);
          if (!isValidNodeContextJsonDocument(parsed)) {
            applyClientValidationFailure(NODE_CONTEXT_JSON_SHAPE_ERROR);
            filePayloadRef.current = null;
            setSelectedFile(null);
            resetFileInput(inputRef);
            return;
          }
        } catch {
          applyClientValidationFailure(nci.clientValidation.invalidJson);
          filePayloadRef.current = null;
          setSelectedFile(null);
          resetFileInput(inputRef);
          return;
        }
      }

      filePayloadRef.current = {
        name: file.name,
        type: file.type,
        buffer: await readFileAsArrayBuffer(file),
      };
      setSelectedFile(file);
      setValidationPassed(false);
      setStatusTier("neutral");
      setStatusMessage(nci.sessionStatus.neutral);
      resetFileInput(inputRef);
    },
    [applyClientValidationFailure, canAccessDataManagement, nci]
  );

  const handleClear = useCallback(() => {
    filePayloadRef.current = null;
    setSelectedFile(null);
    resetFileInput(inputRef);
    setToast(hideImportToast());
    resetSessionStatus();
  }, [resetSessionStatus]);

  const validateDisabled =
    !selectedFile || validating || committing || !canAccessDataManagement;

  const uploadEnabled =
    canAccessDataManagement &&
    validationPassed &&
    !!selectedFile &&
    !validating &&
    !committing;

  const handleUpload = useCallback(async () => {
    const file = fileFromStoredPayload(filePayloadRef.current);
    if (!uploadEnabled || !file) return;
    setCommitting(true);
    setStatusTier("neutral");
    setStatusMessage(nci.sessionStatus.importInProgress);
    setToast(hideImportToast());
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const { data } = await postContextImportSync(fd);
      const formatted = formatSyncApiResponseToStatus(data);
      setStatusMessage(formatted.display);
      setStatusTier(formatted.tier);
      if (formatted.success) {
        showToast(
          buildImportToastSuccess(
            typeof data?.message === "string"
              ? data.message
              : nci.toast.importCompleted
          )
        );
      } else {
        const msg =
          typeof data?.error === "string" && data.error
            ? data.error
            : nci.toast.importIncomplete;
        showToast(buildImportToastFailure(msg));
      }
    } catch (err) {
      const msg = axiosErrorMessage(err);
      showToast(buildImportToastFailure(msg));
      setStatusTier("error");
      setStatusMessage(`${nci.serverMessages.importFailedPrefix}${msg}`);
    } finally {
      setCommitting(false);
    }
  }, [nci, showToast, uploadEnabled]);

  const statusBg =
    STATUS_TINT[statusTier === "neutral" ? "neutral" : statusTier] ??
    STATUS_TINT.neutral;

  return (
    <ImportsPageRoot data-testid="subtab-content-imports">
      {canAccessDataManagement ? (
        <ImportPrimaryCard>
          <CardTitle component="h2">{nci.titles.importCard}</CardTitle>
          <FieldLabel component="div">{nci.labels.selectFile}</FieldLabel>
          <FileRow>
            <FileNameBox title={selectedFile?.name || ""}>
              {selectedFile?.name || nci.labels.noFileSelected}
            </FileNameBox>
            <UploadButton
              variant="outlined"
              component="label"
              disabled={validating || committing}
              sx={{ flexShrink: 0 }}
            >
              <input
                ref={inputRef}
                type="file"
                accept=".txt,.json,application/json,text/plain"
                hidden
                onClick={(event) => {
                  event.currentTarget.value = "";
                }}
                onChange={handleFileChosen}
              />
              {nci.buttons.chooseFile}
            </UploadButton>
          </FileRow>
          <ActionRow>
            <UploadButton
              variant="outlined"
              disabled={validateDisabled}
              onClick={runServerValidate}
            >
              {nci.buttons.validate}
            </UploadButton>
            <UploadButton
              variant="contained"
              disabled={!uploadEnabled}
              onClick={handleUpload}
            >
              {nci.buttons.upload}
            </UploadButton>
          </ActionRow>
          <ClearLink
            component="button"
            type="button"
            underline="always"
            onClick={handleClear}
          >
            {nci.buttons.clear}
          </ClearLink>
        </ImportPrimaryCard>
      ) : null}

      <ImportCardShell $tintBg={statusBg}>
        <CardTitle component="h2">{nci.titles.statusCard}</CardTitle>
        <StatusBody data-testid="import-status-message">{statusMessage}</StatusBody>
      </ImportCardShell>

      <Toast
        type={toast.type}
        title={toast.title}
        description={toast.description}
        show={toast.show}
        duration={IMPORT_TOAST_DEFAULT_DURATION}
        onClose={() => setToast(hideImportToast())}
      />
    </ImportsPageRoot>
  );
}
